import { useRef, useState } from 'react'
import type { DragEvent } from 'react'
import { ArrowUpTrayIcon, PlusIcon } from '@heroicons/react/16/solid'
import type { DropTarget } from '../../../Services/OrderWizard/dropTargets.ts'
import { poolTitle } from '../../../Services/OrderWizard/pools.ts'
import { WIZARD_DROP_ATTRIBUTE } from './useFileDrag.ts'
import { zoneFrameClass } from './zoneFrame.ts'
import { plural } from '../../../Util/plural.ts'

export const targetId = (target: DropTarget): string =>
  target.kind === 'pool' ? `pool-${target.pool}` : target.kind === 'new' ? 'new' : target.kind

const textOf = (target: DropTarget, compact: boolean): [string, string] => {
  switch (target.kind) {
    case 'order':
      return compact ? ['Drop files here', 'or click to browse'] : ['Drop to add to this order', 'FASTQ files and a sample sheet']
    case 'pool':
      return [compact ? poolTitle(target) : `Add to ${poolTitle(target, { inSentence: true })}`, plural(target.items, 'item')]
    case 'new':
      return [compact ? 'New pool' : 'Start a new pool', `becomes pool ${target.number}`]
  }
}

const zoneOf = (target: EventTarget | null): string | undefined =>
  (target instanceof Element ? target.closest('[data-drop-target]')?.getAttribute('data-drop-target') : undefined) ?? undefined

/**
 * The zone a drag is over. Enter and leave are counted over the whole set of zones, so crossing a zone's
 * children or the gaps between zones never clears it; only leaving the set does. Over a gap the last zone
 * stays hovered: dropping back to the default sizes there would move the gap under the cursor.
 */
export const useZoneHover = () => {
  const depth = useRef(0)
  const [hovered, setHovered] = useState<string>()
  const point = (event: DragEvent) => {
    const zone = zoneOf(event.target)
    if (zone) {
      setHovered(zone)
    }
  }
  return {
    hovered,
    reset: () => {
      depth.current = 0
      setHovered(undefined)
    },
    onDragEnter: (event: DragEvent) => {
      depth.current += 1
      point(event)
    },
    onDragOver: point,
    onDragLeave: () => {
      depth.current = Math.max(0, depth.current - 1)
      if (depth.current === 0) {
        setHovered(undefined)
      }
    }
  }
}

/* literal class names, so Tailwind sees each one */
const DEFAULT_GROW = { large: 'grow-[2]', normal: 'grow' }
const HOVER_GROW = { hovered: 'motion-safe:grow-[3]', other: 'motion-safe:grow' }

export type DropZonesProps = {
  targets: DropTarget[]
  variant: 'overlay' | 'popover'
  disabled?: boolean
  onFiles: (target: DropTarget, files: File[]) => void
  /** Makes each zone a button that opens the file picker for it. */
  onPick?: (target: DropTarget) => void
  className?: string
  /** Sizes each zone keeps, for a layout of many zones that wraps or scrolls. */
  zoneClassName?: string
  testId: string
}

/**
 * One drop zone per target: pool 1 larger by default, the zone under a drag
 * growing while the others shrink. Under reduced motion the sizes stay and only the highlight moves.
 */
export const DropZones = ({ targets, variant, disabled, onFiles, onPick, className, zoneClassName, testId }: DropZonesProps) => {
  const hover = useZoneHover()
  const overlay = variant === 'overlay'
  const hovered = disabled ? undefined : hover.hovered
  const large = (target: DropTarget) => target.kind === 'order' || (target.kind === 'pool' && target.primary)
  const targetOf = (id: string | undefined) => targets.find((target) => targetId(target) === id)
  return (
    <div
      {...(disabled ? {} : { [WIZARD_DROP_ATTRIBUTE]: '' })}
      data-testid={testId}
      className={`flex ${className ?? ''}`}
      onDragEnter={hover.onDragEnter}
      onDragLeave={hover.onDragLeave}
      onDragOver={(event) => {
        hover.onDragOver(event)
        event.preventDefault()
        if (!targetOf(zoneOf(event.target) ?? hover.hovered) && event.dataTransfer) {
          event.dataTransfer.dropEffect = 'none'
        }
      }}
      onDrop={(event) => {
        event.preventDefault()
        event.stopPropagation()
        const target = targetOf(zoneOf(event.target) ?? hover.hovered)
        hover.reset()
        const files = Array.from(event.dataTransfer.files ?? [])
        if (target && !disabled && files.length > 0) {
          onFiles(target, files)
        }
      }}
    >
      {targets.map((target) => {
        const id = targetId(target)
        const isHovered = hovered === id
        const isLarge = large(target)
        const [title, detail] = textOf(target, !overlay)
        const Icon = target.kind === 'new' ? PlusIcon : ArrowUpTrayIcon
        const shared = {
          'data-drop-target': id,
          'data-testid': `${testId}-${id}`,
          'data-hovered': isHovered ? 'true' : undefined,
          'data-large': isLarge ? 'true' : undefined,
          className: `flex ${zoneClassName ?? 'min-h-0 min-w-0'} basis-0 flex-col items-center justify-center rounded-xl text-center ${
            isLarge ? DEFAULT_GROW.large : DEFAULT_GROW.normal
          } ${hovered ? (isHovered ? HOVER_GROW.hovered : HOVER_GROW.other) : ''} ${overlay ? 'gap-1 p-4' : 'gap-0.5 px-2 py-2'} ${zoneFrameClass(
            isHovered,
            overlay
              ? 'border-dashed border-slate-300 bg-white/85 text-slate-600'
              : 'border-dashed border-slate-300 bg-slate-50 text-slate-600 hover:border-slate-400 focus-visible:border-solid focus-visible:border-slate-500'
          )} ${isHovered ? 'text-slate-900' : ''}`
        }
        const content = (
          <>
            <Icon
              className={`${overlay && isLarge ? 'h-8 w-8' : overlay ? 'h-5 w-5' : 'h-4 w-4'} ${isHovered ? 'text-primary-600' : 'text-slate-400'}`}
              aria-hidden
            />
            <span className={`font-semibold ${overlay ? (isLarge ? 'text-xl' : 'text-base') : 'text-sm'}`}>{title}</span>
            <span className={overlay ? 'text-sm text-slate-500' : 'text-xs text-slate-500'}>
              {disabled ? 'Wait until the files dropped are read' : detail}
            </span>
          </>
        )
        return onPick ? (
          <button key={id} type="button" {...shared} disabled={disabled} onClick={() => onPick(target)}>
            {content}
          </button>
        ) : (
          <div key={id} {...shared}>
            {content}
          </div>
        )
      })}
    </div>
  )
}
